<template>
  <div>
    <header>
      <div class="app-header-branding">
        <a href="http://www.dmv.ri.gov/" title="Go to RI DMV Home Page" target="_blank" rel="noopener noreferrer">
          <img src="@ridmv/web-themes/images/logo.png" alt="RI DMV logo">
        </a>
        <div class="app-header-dmv">
          <div>State of Rhode Island</div>
          <div>Division of Motor Vehicles</div>
          <div>Department of Revenue</div>
        </div>
      </div>

      <div class="app-header-info">
        <div>
          <a href="http://www.dmv.ri.gov/contact/" target="_blank" rel="noopener noreferrer">Contact us</a>
          <a href="http://www.dmv.ri.gov/" target="_blank" rel="noopener noreferrer">RI DMV Home Page</a>
          <div class="signout-dropdown" v-if="showSignoutDropdown">
            <button class="dropdownbtn" @click="dropdownFunction">
              {{ initialsValue }}
            </button>
            <div class="dropdown-content" id="myDropdown" :class="$store.state.guestLogin.step <= 4 ? '' : 'left50'" >
            <button ref="button" @click="getLogout()">Logout</button> 
            </div>
            <div class="cart" v-if="$store.state.guestLogin.step <= 4">
              <span class="cart-count" v-if="$store.state.guestLogin.renewalCart">{{ $store.state.guestLogin.renewalCart.length == 0 ? '': $store.state.guestLogin.renewalCart.length }}</span>
              <input type="image" src="static/CartImageNew.png" class="cart-icon" :alt="`Cart contains ${$store.state.guestLogin.renewalCart.length} items`" @click="goToCart()" @keyup.enter="goToCart()"/>
            </div>
          </div>
        </div>
      </div>
    </header>

    <ModalExtendedLogin :showModalActive="activeModal" @getShowStatus="showGetModal" ref="extendedModal" :on-timeout="checkModal"/>
    <RenewRegConfirmationModal  :showRenewRegModalActive="renewRegActiveModal" @getRenewRegShowStatus="showRenewRegModal" ref="confirmationModal"/>
    <SessionInactiveModal :showSessionModalInactive="inactiveModal" @getShowInactiveStatus="showGetInactiveSessionModal" ref="sessionModalRef" :on-timeout="checkSessionInactiveModal"/>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'; 
import { useStore } from 'vuex'; 
import { useRouter, useRoute } from 'vue-router'; 
import * as COMMONS from '../store/_commons'; 
import ModalExtendedLogin from '../components/appModal/ModalExtendedLogin.vue'; 
import RenewRegConfirmationModal from '../components/appModal/RenewRegConfirmationModal.vue';
import SessionInactiveModal from './appModal/SessionInactiveModal.vue';

// Vuex store
const store = useStore(); 
// Vue router
const router = useRouter(); 
const route = useRoute();

const signoutRoutes = [
  'BusinessProfile',
  'Profile',
  'RenewReg',
  'DuplicateReg',
  'CancelReg',
  'DuplicateTitle',
  'UpdateAddress',
  'UpdateCommunication',
  'BusinessUpdateContacts'
];
const showSignoutDropdown = computed(() =>
  signoutRoutes.includes(route.name) ||
  (route.name === 'UpdateContacts' && !store.state.guestLogin.createContact)
);

// eslint-disable-next-line no-unused-vars
const riPackage = '~@ridmv/web-themes'; 

// Reactive state 
const activeModal = ref(false); 
const activeTimer = ref(null); 
const renewRegActiveModal = ref(false);
const inactiveModal = ref(false);
const inactiveTimer = ref(null);
const inactiveDuration = ref(15);

// Refs for components
const extendedModal = ref(null);
const confirmationModal = ref(null);
const sessionModalRef = ref(null);

// Computed properties
const personalInfo = computed(() => store.getters[COMMONS.GUEST_PERSONAL_INFO]); 
const statusInfo = computed(() => store.getters[COMMONS.GUEST_LOGIN_STATUS_INFO]);
const businessInfo = computed(() => store.getters[COMMONS.BUSINESS_PROFILE_INFO]);

const initialsValue = computed(() => { 
  let result = '';
  
  // business info
  if (businessInfo.value?.businessPersonalInformation) {
    const orgName = businessInfo.value.businessPersonalInformation.organizationName;
    if (orgName && orgName.length >= 2) {
      result = orgName.charAt(0) + orgName.charAt(1);
    } else if (orgName && orgName.length === 1) {
      result = orgName.charAt(0);
    }
  } 
  // personal info 
  else if (personalInfo.value?.personalInformation) { 
    result = personalInfo.value.personalInformation.firstName.charAt(0) + 
             personalInfo.value.personalInformation.lastName.charAt(0);
  } 
  // statusInfo
  else if (statusInfo.value?.customerName) {
    const name = statusInfo.value.customerName;
    result = name.charAt(0) + (name.length > 1 ? name.charAt(1) : '');
  }
  
  return result.toUpperCase();
});

const showExtensionModalActive = computed(() => { 
  return store.state.guestLogin.accessToken
});
const showInactiveSessionModal = computed(() => {
  return store.state.guestLogin.accessToken != null && store.state.guestLogin.accessToken != ''}); 

// Methods
const getGuestLogout = () => store.dispatch(COMMONS.GUEST_LOGIN_GET_LOGOUT); 
const getCancelTransaction = () => store.dispatch(COMMONS.GUEST_CANCELTRANSACTION);

const getLogout = () => { 
  getGuestLogout();
};

const dropdownFunction = () => {
  document.getElementById("myDropdown").classList.toggle("show");
};

const showGetModal = (val) => {
  clearTimeout(activeTimer.value);
  clearInterval(activeTimer.value);
  activeModal.value = val; // Update the value of ref
};

const showGetInactiveSessionModal = (val) => {
  clearTimeout(inactiveTimer.value);
  clearInterval(inactiveTimer.value);
  inactiveModal.value = val;
};

const checkModal = () => {
  if (activeModal.value) {
    getGuestLogout();
    clearInterval(activeTimer.value);
  }
};

const checkSessionInactiveModal = () => {
  if (inactiveModal.value) {
    getGuestLogout();
    clearInterval(inactiveTimer.value);
  }
};

const showExtensionModal = async () => {
  let duration = statusInfo.value.duration; 
  let totalDelayMilliSeconds = (duration * 60000) - 60000;
  activeTimer.value = setTimeout(() => { // Use setInterval
    if (!inactiveModal.value) {
      activeModal.value = true;
      clearInterval(inactiveTimer.value);

      nextTick(() => {
        if (extendedModal.value) {
          extendedModal.value?.startTimer();
        }
        else {
          checkModal()
        }
      })
    }
  }, totalDelayMilliSeconds);
};

const showSessionInactiveModal = async () => {
  if (showInactiveSessionModal.value && !activeModal.value) {
    let totalDelayMilliSeconds  = (inactiveDuration.value * 60000) - 60000;
    inactiveTimer.value = setTimeout(() => {
      inactiveModal.value = true;

      nextTick(() => {
        if (sessionModalRef.value) {
          sessionModalRef.value?.startTimer();
        }
        else {
          checkSessionInactiveModal();
        }
      });
    }, totalDelayMilliSeconds)
  }
};

const goToCart = () => {
  const guest = store.state.guestLogin;
  const hasRenewalCart = guest.renewalCart && guest.renewalCart.length > 0;

  if (!hasRenewalCart) return;

  const showRenewRegModal = guest.step > 1 && guest.step < 4;
  if (showRenewRegModal) {
    renewRegActiveModal.value = true;
    return;
  }

  guest.step = 4;

  const isOnRegRoute = route.name === 'RenewReg' || route.name === 'DuplicateReg' || route.name === 'CancelReg';

  if (isOnRegRoute) return;

  navigateToRegRoute(guest);
};

const navigateToRegRoute = (guest) => {
  if (guest.duplicateReg) {
    router.push({ name: 'DuplicateReg' });
    return;
  }

  if (guest.cancelReg) {
    router.push({ name: 'CancelReg' });
    return;
  }

  router.push({ name: 'RenewReg' });
};

const showRenewRegModal = async (val) => {
  renewRegActiveModal.value = false;
  if (val == 'Yes') {
    await getCancelTransaction().then(() => {
      store.state.guestLogin.step = 4;
    })
  }
};

// Watcher
watch(showExtensionModalActive, (newVal, oldVal) => { 
  console.log(oldVal);
  if (newVal !== '') {
    showExtensionModal();
    showSessionInactiveModal();
  }
});

// Lifecycle Hooks
onMounted(() => { 
  window.onclick = function (event) {
    if (!event.target.matches('.dropdownbtn')) {
      const dropdowns = document.getElementsByClassName("dropdown-content");

      for (const openDropdown of dropdowns) {
        if (openDropdown.classList.contains('show')) {
          openDropdown.classList.remove('show');
        }
      }
    }
  };

  // Session inactivity logic.
  const setupInactivityTimer = () => {
    // Clear previous handlers if any
    window.removeEventListener('click', handleUserActivity);
    window.removeEventListener('mousedown', handleUserActivity);
    window.removeEventListener('keydown', handleUserActivity);

    // Add new handlers
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('mousedown', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
  };

  const handleUserActivity = () => {
    clearInterval(inactiveTimer.value);
    clearTimeout(inactiveTimer.value);
  };


  if (showExtensionModalActive.value) { 
    showExtensionModal();
    showSessionInactiveModal();
  }
  setupInactivityTimer(); 
});

onUnmounted(() => { 
  clearInterval(activeTimer.value);
  clearInterval(inactiveTimer.value);
  clearTimeout(); 
});
</script>


<style lang="scss" scoped>
@import "~@ridmv/web-themes/styles/header";

.app-header-info{
    .cart {
    height: 40px;
    position: relative;
    padding: 0.5rem;
    .cart-count {
      position: absolute;
      top: 0%;
      font-weight: bold;
      text-align: center;
      width: 47px;
      color: white;
      font-size: 14px;
    }
    
    .cart-icon{
      width: 46px;
      height: 46px;
      vertical-align: middle;
      top: 10%;
      position: absolute;
      cursor: pointer;

      &:focus {
        outline-color: orange;
      }
    }
  }
}
.signout-dropdown {
  position: relative;
  display: inline-flex;

  .dropdownbtn {
    background-color: var(--ri-light-blue);
    border-radius: 50%;
    font-size: 1rem;
    border: 3px solid white;
    padding: 0.5em 0.2em;
    height: 40px;
    width: 40px;
    color: #000;
    min-width: 40px;

    &:hover {
      background-color: orange;
      border: 3px solid orange;
    }
  }

  .left50 {
    left: 33% !important;
  }

  .show {
    display: block !important;
  }
  .dropdown-content {
    display: none;
    position: absolute;
    left: 27%;
    z-index: 1000;
    width: 185px;
    padding: 1rem;
    box-shadow: 7px 10px 8px 0px rgb(0 0 0 / 20%);
    background-color: white;
    transform: translate(-50%, 15%);
    margin-top: 3rem;
    ::after {
      content: '';
      width: 15px;
      height: 15px;
      position: absolute;
      transform: rotate(135deg);
      background-color: white;
      z-index: 999;
      top: -6px;
      right: 27%;
    }
    a {
      color: #000;
      font-size: 18px;
      margin: 5px;
    }
    button {
      min-width: 145px;
      border: 0px solid;
    }
    
    @media only screen and (max-width: 800px) {
      ::after {
        content: '';
        right: 44%;
      }
    }
  }
}
header {
  @media only screen and (max-width: 800px) {
    .app-header-info {
      a {
        white-space: normal;
      }
      .cart {
        .cart-count{
          position: absolute;
          top: 0%;
          font-weight: bold;
          text-align: center;
          width: 47px;
          color: #000;
          font-size: 14px;
        }
      }
    }
  }     
  @media only screen and (min-width: 801px) and (max-width:960px) {
    .app-header-info {
      a {
        white-space: normal;
        display: block;
        width: 100%;
      }
    }
  }   
}
</style>
